// src/types/errorDefinitions.ts

/**
 * Error Definition Types
 * Types for the Error Definitions API (/api/v1/ui/error-definitions)
 */

// Error severity levels — MUST match the backend ErrorSeverity enum exactly
// (Info | Warning | Critical). The create/update API rejects any other value
// with "Invalid severity".
export type ErrorSeverity = 'Critical' | 'Warning' | 'Info';

// Error category types (must match backend ErrorCategory enum)
export type ErrorCategory =
  | 'Encoder'        // Card encoder related errors (SALTO, etc.)
  | 'Mechanical'     // Mechanical errors (jams, motor failures)
  | 'Card'           // Card related errors (not inserted, invalid, etc.)
  | 'Room'           // Room/Reservation related errors
  | 'Network'        // Network connectivity errors
  | 'System'         // System/Internal errors
  | 'PMS'            // Property Management System related errors
  | 'Locks'          // Lock system related errors (SALTO, etc.)
  | 'QR'             // QR code scanning/validation errors
  | 'Payments'       // Payment-related errors in the kiosk
  | 'Identification';// Identity document reading/validation errors

// Error nature — distinguishes real errors from operational events
export type ErrorNature = 'Error' | 'Operational' | 'Informational';

/**
 * Comparison operator for a metadata match condition.
 * Backend enum is extensible, but only "Equals" is supported today.
 */
export type MatchConditionOperator = 'Equals';

/**
 * A metadata match condition attached to an error definition.
 * Conditions narrow when a definition applies by matching event metadata
 * (exact equality, ANDed across all active conditions).
 */
export interface MatchCondition {
  id?: string; // Present on existing conditions returned by the API
  metadataKey: string;
  metadataValue: string;
  operator: MatchConditionOperator;
  isActive?: boolean;
}

/**
 * Severity color mapping
 */
export const SEVERITY_COLORS: Record<ErrorSeverity, string> = {
  Critical: '#F44336', // Red   — matches backend GetColorCode
  Warning: '#FFA726',  // Orange — matches backend GetColorCode
  Info: '#0288D1',     // Blue  — matches backend GetColorCode
};

/**
 * Category icon mapping (Material Icons names)
 */
export const CATEGORY_ICONS: Record<ErrorCategory, string> = {
  Encoder: 'credit_card',
  Mechanical: 'build',
  Card: 'credit_card',
  Room: 'meeting_room',
  Network: 'wifi_off',
  System: 'computer',
  PMS: 'apartment',
  Locks: 'lock',
  QR: 'qr_code',
  Payments: 'payments',
  Identification: 'badge',
};

/**
 * Supported languages for translations
 */
export const SUPPORTED_LANGUAGES = ['es', 'en', 'fr', 'de', 'it', 'pt'] as const;
export type SupportedLanguage = typeof SUPPORTED_LANGUAGES[number];

/**
 * Translation for an error definition
 */
export interface ErrorDefinitionTranslation {
  id?: string;
  language?: SupportedLanguage;
  languageCode?: string; // API returns this field
  title: string;
  description: string;
  suggestedAction?: string;
  recommendation?: string; // API returns this field
}

/**
 * Single error definition from API
 * Matches backend structure from GET /api/v1/ui/error-definitions
 */
export interface ErrorDefinition {
  id?: string;
  errorCode: string;
  deviceType?: string;
  vendor?: string;
  vendorCode?: string;
  severity: ErrorSeverity;
  category: ErrorCategory;
  nature?: ErrorNature;
  defaultTitle?: string;
  defaultDescription?: string;
  suggestedAction?: string;
  isActive?: boolean;
  displayOrder?: number;
  createdAt?: string;
  updatedAt?: string;
  translations: ErrorDefinitionTranslation[];
  conditions?: MatchCondition[];
}

/**
 * Error definition lookup result
 * From GET /api/v1/ui/error-definitions/{errorCode}/translation?language={lang}
 */
export interface ErrorDefinitionLookup {
  errorCode: string;
  severity: ErrorSeverity;
  category: ErrorCategory;
  title: string;
  description: string;
  suggestedAction?: string;
}

/**
 * Batch lookup request
 */
export interface ErrorDefinitionBatchRequest {
  errorCodes: string[];
  language?: SupportedLanguage;
}

/**
 * Batch lookup response
 */
export interface ErrorDefinitionBatchResponse {
  definitions: Record<string, ErrorDefinitionLookup>;
  missing: string[];
}

/**
 * List response from API
 */
export interface ErrorDefinitionListResponse {
  data: ErrorDefinition[];
  pagination?: {
    page: number;
    pageSize: number;
    totalCount: number;
    totalPages: number;
  };
}

/**
 * Request to create a new error definition
 */
export interface ErrorDefinitionCreateRequest {
  errorCode: string;
  deviceType: string; // Use "*" for all device types
  severity: ErrorSeverity;
  category: ErrorCategory;
  nature?: ErrorNature;
  translations: ErrorDefinitionTranslation[];
  // New conditions only need key/value/operator; id and isActive are ignored on create.
  conditions?: MatchCondition[];
}

/**
 * Request to update an existing error definition
 */
export interface ErrorDefinitionUpdateRequest {
  severity?: ErrorSeverity;
  category?: ErrorCategory;
  nature?: ErrorNature;
  defaultTitle?: string;
  defaultDescription?: string;
  suggestedAction?: string;
  translations?: ErrorDefinitionTranslation[];
  // REPLACE-ALL semantics: send the full list (empty array clears all conditions).
  conditions?: MatchCondition[];
}

/**
 * Helper function to get severity color
 */
export const getSeverityColor = (severity: ErrorSeverity): string => {
  return SEVERITY_COLORS[severity] || SEVERITY_COLORS.Info;
};

/**
 * Helper function to get category icon
 */
export const getCategoryIcon = (category: ErrorCategory): string => {
  return CATEGORY_ICONS[category] || 'help_outline';
};

/**
 * Helper function to get severity background color (with opacity)
 */
export const getSeverityBgColor = (severity: ErrorSeverity): string => {
  const color = SEVERITY_COLORS[severity] || SEVERITY_COLORS.Info;
  return `${color}20`;
};

/**
 * Helper function to check if severity is critical (for KPI calculations)
 */
export const isCriticalSeverity = (severity: ErrorSeverity): boolean => {
  return severity === 'Critical';
};
